import React, { useCallback, useEffect, useState } from "react"
import { Button, Col, Row } from "reactstrap"
import { Link, Outlet } from "react-router-dom"
import { oldRoutes } from "router/old-routes"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import Boat from "components/boats/Boat"
import WithEmptyState from "components/common/WithEmptyState"
import FirstLoading from "modules/loader-watchers/FirstLoading"

import { useTitle } from "hooks/useTitle"
import { useTranslation } from "react-i18next"
import { isLastPage } from "helpers/pagination"
import { Waypoint } from "react-waypoint"

// Redux
import { useDispatch, useSelector } from "react-redux"
import { cleanBoat, getGuideBoats } from "store/boats"
import { collectionAnySelector, collectionSelector, metadataSelector } from "store/selectors"
import { VERIFICATION_STATUS } from "layoutsV2/pro/PersonalInfoLayout/constants"
import useActiveGuide from "hooks/useActiveClient"
import AddEquipmentModal from "pagesV2/pro/Equipment/AddEquipmentModal"
import { isFeatureEnabled } from "config/features"
import { toaster } from "components/common/Toast"

export default function Boats() {
  useTitle("Boats")
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const metadata = useSelector(metadataSelector("boats"))
  const boats = useSelector(collectionSelector("boats"))
  const hasBoats = useSelector(collectionAnySelector("boats"))
  const { guide } = useSelector((state) => state.user)
  const status = guide?.verification_status
  const isPending = status === VERIFICATION_STATUS.PENDING
  const [active] = useActiveGuide()
  const [isAddEquipmentModalVisible, setIsAddEquipmentModalVisible] = useState(false)

  const fetchBoats = useCallback((nextPage = false) => dispatch(getGuideBoats(nextPage)), [dispatch])

  const handleSaveEquipment = (formData) => {
    toaster.success("Equipment added successfully")
    setIsAddEquipmentModalVisible(false)
  }

  useEffect(() => {
    fetchBoats()
    return () => dispatch(cleanBoat())
  }, []) //eslint-disable-line

  return (
    <div className="flex-fill z-0 w-100 px-20 py-10" id="boats">
      <div className="sticky-top">
        <div className="hstack align-items-center bg-white py-10 py-sm-20">
          <h1 className="h3 fw-medium lh-1">Boats</h1>
          <Button
            tag={Link}
            color="primary"
            className="px-20 py-10 fs-7 ms-auto my-n1"
            to={active && !isPending ? oldRoutes.guideAddNewBoatPath() : undefined}
            disabled={!active || isPending}
          >
            {t("boats.add_new")}
          </Button>
          {isFeatureEnabled("NEW_RESOURCES") && (
            <Button color="primary" className="px-20 py-10 fs-7 ms-2 my-n1" onClick={() => setIsAddEquipmentModalVisible(true)}>
              {t("equipment.add_equipment")}
            </Button>
          )}
        </div>
      </div>
      <FirstLoading name="boats">
        <WithEmptyState name="boat" hasItems={hasBoats} className="vstack gap-20">
          <Row className="gap-y-20 gx-20 gap-y-xl-35 gx-xl-35 mt-25">
            {boats.map((boat) => (
              <Col xs={12} sm={6} xl={3} key={boat.id}>
                <Link
                  className={"text-dark text-decoration-none bg-opacity-0 bg-opacity-5-hover bg-primary-second"}
                  to={oldRoutes.guideEditBoatPath({ id: boat.id })}
                >
                  <Boat boat={boat} className="h-100" />
                </Link>
              </Col>
            ))}
          </Row>

          {!isLastPage(metadata) && <Waypoint onEnter={() => fetchBoats(true)} />}
        </WithEmptyState>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>

        {isFeatureEnabled("NEW_RESOURCES") && (
          <AddEquipmentModal
            visible={isAddEquipmentModalVisible}
            onCancel={() => setIsAddEquipmentModalVisible(false)}
            onSave={handleSaveEquipment}
          />
        )}
      </FirstLoading>
    </div>
  )
}
